import { API_URL } from '../api';

/**
 * Plugin pages in DiSI's frames (documentation/plugins.md §4 and §7).
 *
 * A page is served behind the session at /plugin/<id>/<scope>/<page>; the
 * scope - the department the page works for, or "g" - decides whether the
 * plugin is on and which settings it gets. Messages go to that origin only and
 * are taken from it only.
 *
 * Protocol (all messages are objects with `type`):
 *   plugin → DiSI  disi:ready                           the page is loaded
 *   DiSI → viewer  disi:show {data, space, lang}        the block to show
 *   page → DiSI    disi:height {height}                 the page's height
 *   DiSI → editor  disi:load {data, media, lang}        the block to edit (data null: a new one);
 *                                                       media: {field: address of the stored file}
 *   editor → DiSI  disi:changed {data, media?}          after every change; media: {field:
 *                                                       {base64, type}} for a new file to store
 */

const BASE = `${API_URL.replace(/\/+$/, '')}/plugin/`;

export const PLUGIN_ORIGIN = new URL(BASE, window.location.href).origin;

/** The address of one of the plugin's pages, for a department (or "g"). */
export function pluginUrl(pluginId, scope, page = '') {
  const path = page.replace(/^\/+/, '');
  return `${BASE}${encodeURIComponent(pluginId)}/${scope ?? 'g'}/${path}`;
}

/** Whether a message event comes from this frame. */
export function fromFrame(event, frame) {
  return (
    event.origin === PLUGIN_ORIGIN &&
    Boolean(frame) &&
    event.source === frame.contentWindow &&
    typeof event.data?.type === 'string'
  );
}

/** The block's data as a reader may see it: without the fields only the author needs. */
export function readerData(data, block) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return data;
  const shown = { ...data };
  for (const field of block?.private ?? []) delete shown[field];
  return shown;
}

/** A file the editor handed over (base64) as a File to upload. */
export function mediaFile({ base64, type = 'image/jpeg' }, field) {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const extension = { 'image/png': 'png', 'image/webp': 'webp', 'image/gif': 'gif' }[type] ?? 'jpg';
  return new File([bytes], `${field}.${extension}`, { type });
}

/** A text of a manifest ({de, en} or a string) in the interface's language. */
export function pluginText(text, lang) {
  if (!text) return '';
  if (typeof text === 'string') return text;
  return text[lang] ?? text.de ?? text.en ?? '';
}
